import {
  Button,
  Flex,
  Heading,
  IconButton,
  toast
} from '@sparrowengg/twigs-react';
import { HomeIcon, SettingsIcon } from '@sparrowengg/twigs-react-icons';
import CloudUploadIcon from 'commons/icons/cloud-upload';
import TickIcon from 'commons/icons/tick';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  COMPILE_SUCCESS_MESSAGE,
  dashboardFields,
  developedAppTypes,
} from '../constants';
import { useDispatch } from 'react-redux';
import { setAppValidation, setCurrentApp } from 'redux-store/actions';
import { wrapCodeInsideFunction } from 'commons/helper';
import { useEffect, useState } from 'react';
import {
  iparamType,
  productConstants
} from 'commons/constants';
import { IRootDispatch, IRootState } from 'commons/types';
import { compileSSDKFile, saveSSDKFile, zipSSDKFile } from '../services';
import { SingleAppDTOType } from 'features/dashboard/types';
import { FieldType } from '../types';

const Header = ({
  handleCurrentField,
  validateRef,
  publishAppRef,
  currentField,
  headerBtnLoader
}: {
  handleCurrentField: (type: string, field: string) => void;
  validateRef: React.RefObject<HTMLButtonElement>;
  publishAppRef: React.RefObject<HTMLButtonElement>;
  currentField: FieldType;
  headerBtnLoader: boolean;
}) => {
  const [loader, setLoader] = useState({
    validation: false,
    publish: false
  });

  const navigate = useNavigate();
  const dispatch = useDispatch<IRootDispatch>();
  const { iparams, oauth } = useSelector(
    (state: IRootState) => state.configurations
  );
  const { whitelistedDomains, dependencies } = useSelector(
    (state: IRootState) => state.settings
  );
  const appValidation = useSelector((state: IRootState) => state.appValidation);
  const currentApp = useSelector((state: IRootState) => state.currentApp);
  const eventFields = useSelector((state: IRootState) => state.eventFields);
  const currentDomain = window.location.hostname?.split('.')[1] || productConstants.SURVEYSPARROW;

  const compileCurrentApp = async (appDetails: {
    appId: number;
    appVersionId: number;
  }) => {
    try {
      const response = (await compileSSDKFile(appDetails.appVersionId))?.data;
      if (response?.message === COMPILE_SUCCESS_MESSAGE) {
        dispatch(
          setAppValidation({
            isValidated: true,
            message: response?.message ?? ''
          })
        );
      }
      toast({
        variant: 'success',
        description: 'App updated successfully'
      });
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error ?? '';
      toast({
        variant: 'error',
        description: 'Error when compiling app'
      });
      dispatch(
        setAppValidation({
          isValidated: false,
          error: errorMessage
        })
      );
    } finally {
      setLoader((prev) => ({ ...prev, validation: false }));
    }
  };

  const publishCurrentApp = async () => {
    setLoader((prev) => ({ ...prev, publish: true }));
    try {
      const response = await zipSSDKFile(`${currentApp.appVersionId}`);

      // Create a Blob from the binary data
      const blob = new Blob([response.data], { type: 'application/zip' });

      const reader = new FileReader();
      reader.readAsBinaryString(blob);
      reader.onload = () => {
        const base64 = btoa(`${reader.result}`);
        sessionStorage.setItem('downloadedZipBlob', base64);
      };
      setTimeout(() => {
        navigate(`/edit-app/${currentApp?.appId}?fileName=download.zip`);
      }, 200);
    } catch (error) {
      console.error('Error downloading zip:', error);
      toast({
        variant: 'error',
        description: 'Error while downloading the zip file'
      });
    } finally {
      setLoader((prev) => ({ ...prev, publish: false }));
    }
  };

  const validateApp = async () => {
    setLoader((prev) => ({ ...prev, validation: true }));
    try {
      const events = eventFields.reduce((acc, currentEvent) => {
        return {
          ...acc,
          [`${currentEvent.field}`]: {
            handler: currentEvent.field
          }
        };
      }, {});
      const currentDependencies = dependencies.reduce(
        (acc, currentDependency) => {
          acc = {
            ...acc,
            [currentDependency.dependency]: currentDependency.version
          };
          return acc;
        },
        {}
      );

      const fileContent = `exports = { ${eventFields.reduce(
        (acc, currentEvent) => {
          acc = `${acc} \n${wrapCodeInsideFunction(
            `${currentEvent.code}`,
            `${currentEvent.field}`
          )},`;
          return acc;
        },
        ''
      )}}`;
      const iparamsFields = iparams.reduce((acc, currentIparam) => {
        const {
          displayName,
          description,
          required,
          secure,
          type,
          keyName,
          product,
          skipApi
        } = currentIparam;
        acc = {
          ...acc,
          [keyName]: {
            display_name: displayName,
            description: description,
            required: required.value,
            secure: secure.value,
            type: type.value,
            type_attributes: {
              product: product.value
            },
            ...(type.value === iparamType.API_KEY &&
              skipApi?.value && { 'data-bind': 'product.api_key' })
          }
        };
        return acc;
      }, {});
      const formattedPayload = {
        appManifestDTO: {
          'platform-version': '1.0',
          product: {
            // getting the domain name from the url
            [currentDomain]: {
              events,
              functions: {}
            }
          },
          'whitelisted-domains': whitelistedDomains.map(
            (domain) => domain.domain
          ),
          dependencies: currentDependencies,
          engines: {
            node: '18.20.4',
            ssdk: '0.2.2'
          }
        },
        files: [
          {
            fileName: 'server.js',
            fileContent
          }
        ],
        iparams: iparamsFields ?? {},
        ...(!!oauth.clientId && {
          oauthConfigDTO: {
            client_id: oauth.clientId,
            client_secret: oauth.clientSecret,
            authorize_url: oauth.authorizationUrl,
            token_url: oauth.tokenUrl,
            options: {
              response_type: 'code',
              pkce: oauth.pkce,
              code_verifier: oauth.codeVerifier,
              code_challenge_method: oauth.codeChallengeMethod,
              scope: oauth.scopes
            }
          }
        }),
        codeType: currentApp?.appId
          ? developedAppTypes.SAME_VERSION
          : developedAppTypes.NEW_APP,
        ...{ ...currentApp }
      };
      const response = (
        await saveSSDKFile(formattedPayload as SingleAppDTOType)
      ).data;
      const appDetails = {
        appId: response?.appId,
        appVersionId: response?.appVersionId
      };
      dispatch(setCurrentApp(appDetails));
      compileCurrentApp(appDetails);
    } catch (error) {
      console.error(error);
      setLoader((prev) => ({ ...prev, validation: false }));
      toast({
        variant: 'error',
        description: 'Error when validating the app'
      });
    }
  };

  useEffect(() => {
    const handleKeydown = (event: any) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        if (eventFields.length) {
          validateApp();
        }
      }
    };
    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [eventFields]);

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      css={{
        padding: '$8 $12',
        borderBottom: '$borderWidths$xs solid $white200'
      }}
    >
      <Flex alignItems="center" gap="$4">
        <IconButton
          icon={<HomeIcon />}
          size="lg"
          color="light"
          variant="ghost"
          onClick={() => navigate('/')}
        />
        <Heading size="h6">AppNest Codespace</Heading>
      </Flex>
      <Flex alignItems="center" gap="$4">
        <Button
          size="lg"
          variant="ghost"
          leftIcon={<CloudUploadIcon color="#4A9CA6" />}
          disabled={
            !appValidation.isValidated ||
            loader.publish ||
            loader.validation ||
            headerBtnLoader
          }
          loading={loader.publish}
          ref={publishAppRef}
          onClick={() => {
            publishCurrentApp();
          }}
        >
          Publish
        </Button>
        <Button
          size="lg"
          ref={validateRef}
          disabled={
            !eventFields?.length || loader.validation || headerBtnLoader
          }
          onClick={validateApp}
          leftIcon={<TickIcon color="white" />}
          loading={loader.validation}
        >
          Save Changes
        </Button>

        <IconButton
          icon={<SettingsIcon />}
          variant="ghost"
          onClick={() =>
            handleCurrentField(
              dashboardFields.SETTINGS,
              dashboardFields.SETTINGS
            )
          }
          color="light"
          size="lg"
        />
      </Flex>
    </Flex>
  );
};

export default Header;
